import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Upload, FileText, Loader2, Check, Edit3, Download, 
  Plus, Trash2, Eye, RefreshCw, Building2, User, Phone, 
  Mail, MapPin, Calendar, DollarSign, Package, Users
} from "lucide-react";
import { useBusiness } from "../contexts/BusinessContext";
import ComercialClientsTab from "./ComercialClientsTab";
import ComercialProductsTab from "./ComercialProductsTab";

const API = process.env.REACT_APP_BACKEND_URL;

function ComercializadoraTab() {
  const { businessId } = useBusiness();
  const fileInputRef = useRef(null);
  
  // Términos y condiciones prellenados para Comercializadora (editables por cotización)
  const DEFAULT_COMERCIAL_TERMS = `* Precios expresados en MXN (Pesos Mexicanos) e incluyen IVA.
* Forma de pago: Contado anticipado.
* Tiempo estimado de entrega: 2 días hábiles posteriores a la confirmación de pago.
* Entrega a pie de camión en el domicilio indicado por el cliente.
* Maniobras, acarreos internos o equipo de descarga no incluidos.`;
  
  // Estados
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);  // Cotización siendo editada
  const [reformulating, setReformulating] = useState(false);
  
  // Formulario de cotización
  const [formData, setFormData] = useState({
    quotation_number: "",  // Editable solo en Comercializadora
    client_name: "",
    client_company: "",
    client_rfc: "",
    client_email: "",
    client_phone: "",
    client_address: "",
    attention_to: "",
    cc_to: "",
    items: [],
    subtotal: 0,
    discount_percent: 0,
    discount_amount: 0,
    iva_amount: 0,
    total: 0,
    terms: "",
    notes: "",
    validity_days: 15,
    seller_name: "",
    seller_position: "",
    original_supplier: "",  // Proveedor original de la cotización
    original_quotation_number: "",  // Número de cotización del proveedor
    original_date: ""  // Fecha de cotización del proveedor
  });

  // Listado de clientes ya registrados para autocomplete (TODOS los negocios, ordenados con comercializadora primero)
  const [recurrentClients, setRecurrentClients] = useState([]);
  // Catálogo de productos Comercializadora para autocomplete
  const [comercialProducts, setComercialProducts] = useState([]);
  // Número sugerido por el backend
  const [suggestedNumber, setSuggestedNumber] = useState("");

  useEffect(() => {
    if (businessId === "comercializadora") {
      fetchQuotations();
      // Cargar TODOS los clientes (de los 3 negocios) — ordenar primero los de comercializadora
      axios.get(`${API}/api/clients`)
        .then(r => {
          const all = r.data?.clients || r.data || [];
          const ordered = [...all].sort((a, b) => {
            const aIsCom = a.business_id === "comercializadora" ? 0 : 1;
            const bIsCom = b.business_id === "comercializadora" ? 0 : 1;
            return aIsCom - bIsCom;
          });
          setRecurrentClients(ordered);
        })
        .catch(() => setRecurrentClients([]));
      // Cargar catálogo de productos comercial
      axios.get(`${API}/api/comercial-products`)
        .then(r => setComercialProducts(r.data?.products || []))
        .catch(() => setComercialProducts([]));
      // Cargar número sugerido
      fetchSuggestedNumber();
    }
  }, [businessId]);

  const fetchSuggestedNumber = async () => {
    try {
      const r = await axios.get(`${API}/api/comercializadora/next-quote-number`);
      if (r.data?.success) {
        setSuggestedNumber(r.data.next_quotation_number || "");
      }
    } catch (e) {
      console.error("Error fetching suggested number:", e);
    }
  };

  const refreshComercialProducts = async () => {
    try {
      const r = await axios.get(`${API}/api/comercial-products`);
      setComercialProducts(r.data?.products || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/quotations?business_id=comercializadora`);
      if (response.data.success) {
        setQuotations(response.data.quotations);
      }
    } catch (error) {
      console.error("Error fetching quotations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Manejar subida de archivo
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error("Formato no válido. Usa PDF, PNG, JPG o WEBP");
      return;
    }

    setUploadedFile(file);
    setExtractedData(null);
    
    // Extraer datos automáticamente
    await extractDataFromFile(file);
  };

  // Extraer datos del archivo con IA
  const extractDataFromFile = async (file) => {
    setExtracting(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("business_id", "comercializadora");

      const response = await axios.post(
        `${API}/api/comercializadora/extract-quotation`,
        formDataUpload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.success) {
        setExtractedData(response.data.extracted);
        
        // Prellenar formulario SOLO con items, términos y proveedor original
        // Los datos del cliente se dejan VACÍOS porque son para TU cliente (a quien revendes)
        const extracted = response.data.extracted;
        setFormData(prev => ({
          ...prev,
          // Datos del cliente VACÍOS - el usuario ingresará los datos de SU cliente
          client_name: "",
          client_company: "",
          client_rfc: "",
          client_email: "",
          client_phone: "",
          client_address: "",
          attention_to: "",
          // Solo prellenar items, términos y referencia del proveedor
          items: extracted.items || [],
          subtotal: extracted.subtotal || 0,
          iva_amount: extracted.iva_amount || 0,
          total: extracted.total || 0,
          terms: extracted.terms || "",
          notes: extracted.notes || "",
          original_supplier: extracted.supplier_name || "",
          // Guardar cotización original del proveedor como referencia
          original_quotation_number: extracted.quotation_number || "",
          original_date: extracted.date || ""
        }));
        
        setShowForm(true);
        toast.success("Datos extraídos. Ahora ingresa los datos de TU cliente y revisa el equipamiento.");
      }
    } catch (error) {
      console.error("Error extracting data:", error);
      toast.error("Error al extraer datos del archivo");
    } finally {
      setExtracting(false);
    }
  };

  // Reformular textos con IA
  const handleReformulate = async () => {
    if (formData.items.length === 0) {
      toast.error("No hay conceptos para reformular");
      return;
    }

    setReformulating(true);
    try {
      const response = await axios.post(`${API}/api/comercializadora/reformulate`, {
        items: formData.items,
        terms: formData.terms
      });

      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          items: response.data.items,
          terms: response.data.terms
        }));
        toast.success("Textos reformulados");
      }
    } catch (error) {
      console.error("Error reformulating:", error);
      toast.error("Error al reformular textos");
    } finally {
      setReformulating(false);
    }
  };

  // Calcular totales — COMERCIALIZADORA: precio unitario es SIN IVA
  // Total = Subtotal (sin IVA) + IVA 16%
  const calculateTotals = () => {
    // Cada item.total = qty × unit_price (sin IVA)
    const subtotalRaw = formData.items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);

    // Aplicar descuento sobre el subtotal sin IVA
    const discountAmount = subtotalRaw * (formData.discount_percent / 100);
    const subtotalAfterDiscount = subtotalRaw - discountAmount;

    // SUMAR IVA 16% al subtotal
    const ivaAmount = subtotalAfterDiscount * 0.16;
    const total = subtotalAfterDiscount + ivaAmount;

    setFormData(prev => ({
      ...prev,
      subtotal: Math.round(subtotalAfterDiscount * 100) / 100,
      discount_amount: Math.round(discountAmount * 100) / 100,
      iva_amount: Math.round(ivaAmount * 100) / 100,
      total: Math.round(total * 100) / 100
    }));
  };

  useEffect(() => {
    calculateTotals();
  }, [formData.items, formData.discount_percent]);

  // Agregar item
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        code: "",
        description: "",
        technical_specs: "",
        unit: "PZA",
        quantity: 1,
        unit_price: 0,
        total: 0
      }]
    }));
  };

  // Actualizar item
  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // Recalcular total del item
      if (field === "quantity" || field === "unit_price") {
        const qty = field === "quantity" ? parseFloat(value) || 0 : parseFloat(newItems[index].quantity) || 0;
        const price = field === "unit_price" ? parseFloat(value) || 0 : parseFloat(newItems[index].unit_price) || 0;
        newItems[index].total = qty * price;
      }
      
      return { ...prev, items: newItems };
    });
  };

  // Eliminar item
  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Guardar cotización
  const handleSave = async () => {
    if (!formData.client_name && !formData.client_company) {
      toast.error("Ingresa el nombre del cliente o empresa");
      return;
    }
    if (formData.items.length === 0) {
      toast.error("Agrega al menos un concepto");
      return;
    }

    setSaving(true);
    try {
      const quotationData = {
        business_id: "comercializadora",
        quotation_type: "cash",
        manual_quotation_number: formData.quotation_number || undefined,  // Si vacío, backend autogenera
        client_name: formData.client_name || formData.client_company,
        client_company: formData.client_company,
        client_rfc: formData.client_rfc,
        client_email: formData.client_email,
        client_phone: formData.client_phone,
        client_address: formData.client_address,
        attention_contacts: formData.attention_to ? [{ name: formData.attention_to }] : [],
        cc_contacts: formData.cc_to ? [{ name: formData.cc_to }] : [],
        items: formData.items.map(item => ({
          code: item.code || "-",
          concept: item.description,  // Para compatibilidad con PDF
          description: item.description,
          technical_specs: item.technical_specs || "",
          unit: item.unit || "PZA",
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          subtotal: parseFloat(item.total) || 0,  // El PDF usa subtotal
          total: parseFloat(item.total) || 0
        })),
        subtotal: formData.subtotal,
        discount_percent: formData.discount_percent,
        discount_amount: formData.discount_amount,
        iva_amount: formData.iva_amount,
        total: formData.total,
        terms_conditions: formData.terms,  // El PDF busca terms_conditions
        terms: formData.terms,
        notes: formData.notes,
        validity_days: formData.validity_days,
        seller_name: formData.seller_name,
        seller_position: formData.seller_position,
        source: "supplier_quotation",
        original_supplier: formData.original_supplier
      };

      let response;
      if (editingQuotation?.id) {
        // Editar cotización existente
        response = await axios.put(`${API}/api/quotations/${editingQuotation.id}`, quotationData);
        if (response.data.success) {
          toast.success(`Cotización ${editingQuotation.quotation_number} actualizada`);
          resetForm();
          fetchQuotations();
          refreshComercialProducts();
        }
      } else {
        // Nueva cotización
        response = await axios.post(`${API}/api/quotations`, quotationData);
        if (response.data.success) {
          toast.success(`Cotización ${response.data.quotation.quotation_number} creada`);
          resetForm();
          fetchQuotations();
          fetchSuggestedNumber();
          refreshComercialProducts();
        }
      }
    } catch (error) {
      console.error("Error saving quotation:", error);
      toast.error("Error al guardar la cotización");
    } finally {
      setSaving(false);
    }
  };

  // Reset formulario
  const resetForm = () => {
    setShowForm(false);
    setUploadedFile(null);
    setExtractedData(null);
    setEditingQuotation(null);
    setFormData({
      quotation_number: "",
      client_name: "",
      client_company: "",
      client_rfc: "",
      client_email: "",
      client_phone: "",
      client_address: "",
      attention_to: "",
      cc_to: "",
      items: [],
      subtotal: 0,
      discount_percent: 0,
      discount_amount: 0,
      iva_amount: 0,
      total: 0,
      terms: "",
      notes: "",
      validity_days: 15,
      seller_name: "",
      seller_position: "",
      original_supplier: "",
      original_quotation_number: "",
      original_date: ""
    });
  };

  // Descargar PDF
  const handleDownloadPDF = async (quotationId) => {    try {
      // Descargar vía fetch+blob (con JWT). window.open NO envía el token y
      // recibe 401 "No autenticado".
      const response = await fetch(`${API}/api/quotations/${quotationId}/pdf`);
      if (!response.ok) {
        const err = await response.text();
        toast.error(`Error al descargar PDF: ${err.substring(0, 100)}`);
        return;
      }
      const blob = await response.blob();
      // Obtener número de cotización del estado actual o del nombre del archivo
      const cd = response.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `COTIZACION_${quotationId.substring(0, 8)}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (error) {
      console.error(error);
      toast.error("Error al descargar PDF");
    }
  };

  // Cargar una cotización existente en el formulario para editar
  const handleEditQuotation = (q) => {
    setEditingQuotation(q);
    setExtractedData(null);
    // Reconstruir items desde la cotización guardada. En comercializadora los
    // unit_price ya están sin IVA (así los guarda POST /quotations para comercial).
    const items = (q.items || []).map(it => ({
      code: it.code || "",
      description: it.description || it.concept || "",
      technical_specs: it.technical_specs || "",
      unit: it.unit || "PZA",
      quantity: parseFloat(it.quantity) || 1,
      unit_price: parseFloat(it.unit_price) || 0,
      total: parseFloat(it.subtotal) || (parseFloat(it.quantity || 1) * parseFloat(it.unit_price || 0)),
    }));
    // Recalcular subtotal/iva/total localmente para reflejar el estado
    const subtotal = items.reduce((s, i) => s + (i.total || 0), 0);
    const discount_percent = parseFloat(q.discount_percent) || 0;
    const discount_amount = subtotal * (discount_percent / 100);
    const subtotal_con_desc = subtotal - discount_amount;
    const iva_amount = subtotal_con_desc * 0.16;
    const total = subtotal_con_desc + iva_amount;
    // Extraer atención y CCPs
    const atn = (q.attention_contacts && q.attention_contacts[0]?.name) || q.attention_to || "";
    const ccp = (q.cc_contacts && q.cc_contacts.map(c => c.name || c.email).filter(Boolean).join(", ")) || q.cc_to || "";
    setFormData({
      quotation_number: q.quotation_number || "",
      client_name: q.client_name || "",
      client_company: q.client_company || "",
      client_rfc: q.client_rfc || "",
      client_email: q.client_email || "",
      client_phone: q.client_phone || "",
      client_address: q.client_address || "",
      attention_to: atn,
      cc_to: ccp,
      items,
      subtotal: round2(subtotal),
      discount_percent,
      discount_amount: round2(discount_amount),
      iva_amount: round2(iva_amount),
      total: round2(total),
      terms: q.terms_conditions || q.terms || "",
      notes: q.notes || "",
      validity_days: q.validity_days || 15,
      seller_name: q.seller_name || "",
      seller_position: q.seller_position || "",
      original_supplier: q.original_supplier || "",
      original_quotation_number: q.original_quotation_number || "",
      original_date: q.original_date || "",
    });
    setShowForm(true);
    // Refrescar catálogo por si hay productos nuevos
    refreshComercialProducts();
    // Scroll al form
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  };

  const round2 = (n) => Math.round((n || 0) * 100) / 100;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(value || 0);
  };

  // Sub-tab dentro de Comercializadora: "quotes" (default) o "clients"
  const [subTab, setSubTab] = useState("quotes");

  if (businessId !== "comercializadora") {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        <p>Esta sección solo está disponible para HEBEH Comercializadora</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub-tabs Cotizaciones / Clientes */}
      <div className="flex gap-1 bg-slate-900/60 border border-slate-800 rounded-lg p-1 w-fit">
        <button
          onClick={() => setSubTab("quotes")}
          data-testid="comercial-subtab-quotes"
          className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition ${
            subTab === "quotes" ? "bg-emerald-600 text-white" : "text-slate-300 hover:bg-slate-800"
          }`}
        >
          <FileText className="w-4 h-4" />
          Cotizaciones
        </button>
        <button
          onClick={() => setSubTab("clients")}
          data-testid="comercial-subtab-clients"
          className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition ${
            subTab === "clients" ? "bg-emerald-600 text-white" : "text-slate-300 hover:bg-slate-800"
          }`}
        >
          <Users className="w-4 h-4" />
          Clientes
        </button>
        <button
          onClick={() => setSubTab("products")}
          data-testid="comercial-subtab-products"
          className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition ${
            subTab === "products" ? "bg-emerald-600 text-white" : "text-slate-300 hover:bg-slate-800"
          }`}
        >
          <Package className="w-4 h-4" />
          Productos
        </button>
      </div>

      {subTab === "clients" ? <ComercialClientsTab /> : subTab === "products" ? <ComercialProductsTab /> : (
      <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Cotizaciones Comercializadora</h2>
          <p className="text-slate-400 text-sm mt-1">
            Sube cotizaciones de proveedores y genera cotizaciones con formato HEBEH
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => {
              setEditingQuotation(null);
              setFormData(prev => ({
                ...prev,
                quotation_number: suggestedNumber || "",
                terms: prev.terms && prev.terms.trim() ? prev.terms : DEFAULT_COMERCIAL_TERMS,
              }));
              // Refrescar catálogo de productos y lista de clientes al abrir
              refreshComercialProducts();
              axios.get(`${API}/api/clients`)
                .then(r => {
                  const all = r.data?.clients || r.data || [];
                  const ordered = [...all].sort((a, b) => {
                    const aIsCom = a.business_id === "comercializadora" ? 0 : 1;
                    const bIsCom = b.business_id === "comercializadora" ? 0 : 1;
                    return aIsCom - bIsCom;
                  });
                  setRecurrentClients(ordered);
                })
                .catch(() => {});
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
            data-testid="new-comercial-quote-btn"
          >
            <Plus className="w-5 h-5" />
            Nueva Cotización
          </button>
        )}
      </div>

      {/* Formulario de nueva cotización */}
      {showForm && (
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">
              {editingQuotation ? `Editar Cotización ${editingQuotation.quotation_number}` : (extractedData ? "Editar Cotización Extraída" : "Nueva Cotización")}
            </h3>
            <button
              onClick={resetForm}
              className="text-slate-400 hover:text-white"
            >
              Cancelar
            </button>
          </div>

          {/* Número de Cotización - EDITABLE (solo Comercializadora) */}
          <div className="mb-6 bg-emerald-900/10 border border-emerald-800/50 rounded-lg p-4">
            <label className="block text-sm text-emerald-300 mb-2 font-medium">
              📋 Número de Cotización (editable, independiente de los otros negocios)
            </label>
            <div className="flex gap-3 items-end">
              <input
                type="text"
                value={formData.quotation_number}
                onChange={(e) => setFormData({ ...formData, quotation_number: e.target.value.toUpperCase() })}
                placeholder={suggestedNumber || "COM-0001"}
                className="px-3 py-2 bg-slate-900 border border-emerald-700/40 rounded-lg text-white font-mono text-lg uppercase focus:border-emerald-500 focus:outline-none"
                data-testid="comercial-quote-number-input"
                style={{ minWidth: "200px" }}
              />
              <button
                type="button"
                onClick={() => setFormData({ ...formData, quotation_number: suggestedNumber || "" })}
                className="px-3 py-2 text-xs text-emerald-400 hover:text-emerald-300 underline"
                data-testid="restore-suggested-number-btn"
              >
                Usar sugerido ({suggestedNumber || "—"})
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Si lo dejas vacío o usas el sugerido, se asignará automáticamente. Solo afecta a Comercializadora.
            </p>
          </div>

          {/* Zona de carga de archivo */}
          {!extractedData && (
            <div className="mb-6">
              <label className="block text-sm text-slate-400 mb-2">
                Cargar cotización de proveedor (PDF o imagen)
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-500 transition-colors"
              >
                {extracting ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
                    <p className="text-slate-400">Extrayendo datos con IA...</p>
                  </div>
                ) : uploadedFile ? (
                  <div className="flex flex-col items-center gap-3">
                    <FileText className="w-10 h-10 text-emerald-500" />
                    <p className="text-white">{uploadedFile.name}</p>
                    <p className="text-slate-400 text-sm">Haz clic para cambiar archivo</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <Upload className="w-10 h-10 text-slate-500" />
                    <p className="text-slate-400">
                      Arrastra o haz clic para cargar cotización
                    </p>
                    <p className="text-slate-500 text-sm">PDF, PNG, JPG o WEBP</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          )}

          {/* Datos extraídos o formulario manual */}
          <div className="space-y-6">
            {/* Info del proveedor original - Referencia */}
            {formData.original_supplier && (
              <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-4">
                <p className="text-amber-400 text-sm font-medium mb-2">
                  📋 Referencia de Cotización del Proveedor
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-slate-400">Proveedor:</span>
                    <span className="text-white ml-2">{formData.original_supplier}</span>
                  </div>
                  {formData.original_quotation_number && (
                    <div>
                      <span className="text-slate-400">Cotización #:</span>
                      <span className="text-white ml-2">{formData.original_quotation_number}</span>
                    </div>
                  )}
                  {formData.original_date && (
                    <div>
                      <span className="text-slate-400">Fecha:</span>
                      <span className="text-white ml-2">{formData.original_date}</span>
                    </div>
                  )}
                </div>
                <p className="text-amber-500/70 text-xs mt-2">
                  Esta información es solo de referencia interna. Ingresa los datos de TU cliente abajo.
                </p>
              </div>
            )}

            {/* Datos del cliente - TU CLIENTE */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-medium flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Datos de TU Cliente
                  <span className="text-xs text-slate-500 font-normal">(a quien revendes)</span>
                </h4>
                <button
                  type="button"
                  onClick={async () => {
                    if (!formData.client_name && !formData.client_company) {
                      toast.error("Escribe al menos el nombre o empresa antes de guardar");
                      return;
                    }
                    try {
                      const payload = {
                        business_id: "comercializadora",
                        business_name: formData.client_company || formData.client_name,
                        rfc: formData.client_rfc || "",
                        address: { street: formData.client_address || "" },
                        primary_contact: {
                          name: formData.client_name || "",
                          email: formData.client_email || "",
                          phone: formData.client_phone || "",
                          position: formData.attention_to || ""
                        },
                        source: "comercializadora_quotation",
                        notes: ""
                      };
                      const r = await axios.post(`${API}/api/clients`, payload);
                      if (r.data?.success) {
                        toast.success("Cliente guardado como recurrente");
                      }
                    } catch (e) {
                      console.error(e);
                      toast.error("Error al guardar cliente: " + (e?.response?.data?.detail || e.message));
                    }
                  }}
                  data-testid="save-client-recurrent-btn"
                  className="text-xs px-3 py-1.5 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-300 font-medium flex items-center gap-1.5 transition"
                  title="Guardar este cliente para reutilizarlo en futuras cotizaciones"
                >
                  💾 Guardar como cliente recurrente
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Nombre / Contacto</label>
                  <input
                    type="text"
                    list="comercial-clients-list"
                    value={formData.client_name}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Si coincide exactamente con un cliente registrado, autollenar
                      const match = recurrentClients.find(c =>
                        (c.business_name || "").toLowerCase() === val.toLowerCase() ||
                        ((c.primary_contact?.name || "").toLowerCase() === val.toLowerCase() && val.length > 2)
                      );
                      if (match) {
                        const pc = match.primary_contact || {};
                        const addr = match.address || {};
                        // Construir dirección completa a partir de todos los campos
                        const fullAddress = [
                          [addr.street, addr.exterior_number].filter(Boolean).join(" "),
                          addr.interior_number ? `Int. ${addr.interior_number}` : "",
                          addr.neighborhood ? `Col. ${addr.neighborhood}` : "",
                          addr.postal_code ? `C.P. ${addr.postal_code}` : "",
                          addr.municipality,
                          addr.city,
                          addr.state,
                        ].filter(Boolean).join(", ");
                        setFormData(prev => ({
                          ...prev,
                          client_name: pc.name || match.business_name || val,
                          client_company: match.business_name || prev.client_company,
                          client_rfc: match.rfc || prev.client_rfc,
                          client_phone: pc.phone || prev.client_phone,
                          client_email: pc.email || prev.client_email,
                          client_address: fullAddress || prev.client_address,
                          attention_to: pc.position || prev.attention_to,
                        }));
                        toast.success(`Cliente cargado: ${match.business_name}`);
                      } else {
                        setFormData({ ...formData, client_name: val });
                      }
                    }}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Nombre del cliente"
                  />
                  <datalist id="comercial-clients-list">
                    {recurrentClients.map(c => (
                      <option key={c.id} value={c.business_name || c.primary_contact?.name || ""}>
                        {c.primary_contact?.name ? `${c.business_name} — ${c.primary_contact.name}` : c.business_name}
                      </option>
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Empresa</label>
                  <input
                    type="text"
                    value={formData.client_company}
                    onChange={(e) => setFormData({ ...formData, client_company: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Empresa"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">RFC</label>
                  <input
                    type="text"
                    value={formData.client_rfc}
                    onChange={(e) => setFormData({ ...formData, client_rfc: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="RFC"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">ATN (Atención)</label>
                  <input
                    type="text"
                    value={formData.attention_to}
                    onChange={(e) => setFormData({ ...formData, attention_to: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="A la atención de..."
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">C.C.P.</label>
                  <input
                    type="text"
                    value={formData.cc_to}
                    onChange={(e) => setFormData({ ...formData, cc_to: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Con copia para..."
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Teléfono</label>
                  <input
                    type="text"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Teléfono"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-400 mb-1">Dirección</label>
                  <input
                    type="text"
                    value={formData.client_address}
                    onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Dirección completa"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Email"
                  />
                </div>
              </div>
            </div>

            {/* Conceptos */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-medium flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Conceptos
                </h4>
                <div className="flex gap-2">
                  <button
                    onClick={handleReformulate}
                    disabled={reformulating || formData.items.length === 0}
                    className="flex items-center gap-1 px-3 py-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm rounded-lg"
                  >
                    {reformulating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Reformular con IA
                  </button>
                  <button
                    onClick={addItem}
                    className="flex items-center gap-1 px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg"
                  >
                    <Plus className="w-4 h-4" />
                    Agregar
                  </button>
                </div>
              </div>
              
              {formData.items.length > 0 ? (
                <div className="space-y-4">
                  {formData.items.map((item, index) => (
                    <div key={index} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                      {/* Fila principal del item */}
                      <div className="grid grid-cols-12 gap-3 items-start">
                        <div className="col-span-1">
                          <label className="block text-xs text-slate-500 mb-1">Código</label>
                          <input
                            type="text"
                            value={item.code || ""}
                            onChange={(e) => updateItem(index, "code", e.target.value)}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                            placeholder="-"
                          />
                        </div>
                        <div className="col-span-5">
                          <label className="block text-xs text-slate-500 mb-1">Descripción (concepto corto)</label>
                          <input
                            type="text"
                            list={`comercial-products-list-${index}`}
                            value={item.description || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              // Si coincide con un producto del catálogo, autorrellenar code/unit/precio/specs
                              const match = comercialProducts.find(p =>
                                (p.description || "").toLowerCase() === val.toLowerCase() ||
                                (p.code || "").toLowerCase() === val.toLowerCase()
                              );
                              if (match) {
                                setFormData(prev => {
                                  const newItems = [...prev.items];
                                  newItems[index] = {
                                    ...newItems[index],
                                    description: match.description || val,
                                    code: match.code || newItems[index].code || "",
                                    unit: match.unit || newItems[index].unit || "PZA",
                                    unit_price: match.last_price || newItems[index].unit_price || 0,
                                    technical_specs: match.technical_specs || newItems[index].technical_specs || "",
                                  };
                                  // Recalcular total
                                  const qty = parseFloat(newItems[index].quantity) || 1;
                                  const price = parseFloat(newItems[index].unit_price) || 0;
                                  newItems[index].total = qty * price;
                                  return { ...prev, items: newItems };
                                });
                                toast.success(`Producto del catálogo: ${match.description}`);
                              } else {
                                updateItem(index, "description", val);
                              }
                            }}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                            placeholder="Nombre del producto, marca, modelo"
                            data-testid={`item-description-${index}`}
                          />
                          <datalist id={`comercial-products-list-${index}`}>
                            {comercialProducts.slice(0, 200).map(p => (
                              <option key={p.id} value={p.description}>
                                {p.code ? `${p.code} — ` : ""}{p.description} {p.last_price ? `($${p.last_price.toLocaleString('es-MX')})` : ""}
                              </option>
                            ))}
                          </datalist>
                        </div>
                        <div className="col-span-1">
                          <label className="block text-xs text-slate-500 mb-1">Unidad</label>
                          <input
                            type="text"
                            value={item.unit ?? ""}
                            onChange={(e) => updateItem(index, "unit", e.target.value.toUpperCase())}
                            placeholder="PZA, TON, KG, M²..."
                            list={`unit-list-${index}`}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm text-center"
                          />
                          <datalist id={`unit-list-${index}`}>
                            <option value="PZA" />
                            <option value="TONELADA" />
                            <option value="KG" />
                            <option value="LITRO" />
                            <option value="GALÓN" />
                            <option value="METRO" />
                            <option value="M2" />
                            <option value="M3" />
                            <option value="CAJA" />
                            <option value="PAR" />
                            <option value="JUEGO" />
                            <option value="ROLLO" />
                            <option value="BULTO" />
                            <option value="LOTE" />
                            <option value="SERVICIO" />
                          </datalist>
                        </div>
                        <div className="col-span-1">
                          <label className="block text-xs text-slate-500 mb-1">Cant.</label>
                          <input
                            type="number"
                            value={item.quantity || 1}
                            onChange={(e) => updateItem(index, "quantity", e.target.value)}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm text-center"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs text-slate-500 mb-1 flex items-center justify-between">
                            <span>P. Unitario <span className="text-slate-400">(sin IVA)</span></span>
                            <button
                              type="button"
                              title="¿Es precio con IVA? Convertir a precio sin IVA (÷ 1.16)"
                              onClick={() => {
                                const withIva = parseFloat(item.unit_price) || 0;
                                if (withIva <= 0) return;
                                const withoutIva = Math.round((withIva / 1.16) * 100) / 100;
                                updateItem(index, "unit_price", withoutIva);
                                toast.info(`Convertido: $${withIva.toLocaleString('es-MX')} c/IVA → $${withoutIva.toLocaleString('es-MX')} s/IVA`);
                              }}
                              className="text-orange-400 hover:text-orange-300 text-[10px] font-semibold px-1.5 py-0.5 border border-orange-500/40 rounded leading-none"
                              data-testid={`convert-iva-btn-${index}`}
                            >
                              −IVA
                            </button>
                          </label>
                          <input
                            type="number"
                            value={item.unit_price || 0}
                            onChange={(e) => updateItem(index, "unit_price", e.target.value)}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm text-right"
                          />
                        </div>
                        <div className="col-span-1">
                          <label className="block text-xs text-slate-500 mb-1">Subtotal</label>
                          <div className="px-2 py-1 text-white text-sm text-right">
                            {formatCurrency(item.total || 0)}
                          </div>
                        </div>
                        <div className="col-span-1 flex items-end justify-center pb-1">
                          <button
                            onClick={() => removeItem(index)}
                            className="text-red-400 hover:text-red-300 p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Ficha Técnica (Anexo) - Siempre visible para edición */}
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs text-amber-400 flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            Ficha Técnica / Especificaciones (Anexo PDF)
                          </label>
                          {!item.technical_specs && (
                            <button
                              onClick={() => updateItem(index, "technical_specs", "Ingrese aquí las especificaciones técnicas...")}
                              className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3" />
                              Agregar datos técnicos
                            </button>
                          )}
                        </div>
                        {item.technical_specs !== undefined && (
                          <>
                            <textarea
                              value={item.technical_specs || ""}
                              onChange={(e) => updateItem(index, "technical_specs", e.target.value)}
                              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm resize-y min-h-[120px]"
                              rows={8}
                              placeholder="Especificaciones técnicas, equipamiento incluido, características, normativas, accesorios..."
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              ✓ Este contenido aparecerá como página de ANEXO en el PDF
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  No hay conceptos. Sube una cotización o agrega manualmente.
                </div>
              )}
            </div>

            {/* Totales */}
            <div className="flex justify-end">
              <div className="w-72 space-y-2">
                <div className="flex justify-between text-slate-400">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(formData.subtotal)}</span>
                </div>
                <div className="flex justify-between items-center text-slate-400">
                  <span>Descuento:</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={formData.discount_percent}
                      onChange={(e) => setFormData({ ...formData, discount_percent: parseFloat(e.target.value) || 0 })}
                      className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm text-right"
                    />
                    <span>%</span>
                    <span className="text-red-400">-{formatCurrency(formData.discount_amount)}</span>
                  </div>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>IVA (16%):</span>
                  <span>{formatCurrency(formData.iva_amount)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-emerald-400 border-t border-slate-600 pt-2">
                  <span>TOTAL:</span>
                  <span>{formatCurrency(formData.total)}</span>
                </div>
              </div>
            </div>

            {/* Términos y firma */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Términos y Condiciones</label>
                <textarea
                  value={formData.terms}
                  onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  rows={4}
                  placeholder="Condiciones de venta..."
                />
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Nombre del Vendedor/Autorizador</label>
                  <input
                    type="text"
                    value={formData.seller_name}
                    onChange={(e) => setFormData({ ...formData, seller_name: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Nombre completo"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Puesto</label>
                  <input
                    type="text"
                    value={formData.seller_position}
                    onChange={(e) => setFormData({ ...formData, seller_position: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Ej: Director Comercial"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Vigencia (días)</label>
                  <input
                    type="number"
                    value={formData.validity_days}
                    onChange={(e) => setFormData({ ...formData, validity_days: parseInt(e.target.value) || 15 })}
                    className="w-24 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  />
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
              <button
                onClick={resetForm}
                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Guardar Cotización
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de cotizaciones */}
      {!showForm && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <h3 className="text-white font-medium">Cotizaciones Generadas</h3>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : quotations.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/50">
                  <tr className="text-left text-xs text-slate-400 uppercase">
                    <th className="px-4 py-3">Folio</th>
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Fecha</th>
                    <th className="px-4 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {quotations.map((q) => (
                    <tr key={q.id} className="border-b border-slate-700 hover:bg-slate-700/30">
                      <td className="px-4 py-3 text-emerald-400 font-mono">{q.quotation_number}</td>
                      <td className="px-4 py-3 text-white">{q.client_name || q.client_company}</td>
                      <td className="px-4 py-3 text-white">{formatCurrency(q.total)}</td>
                      <td className="px-4 py-3 text-slate-400 text-sm">
                        {new Date(q.created_at).toLocaleDateString('es-MX')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleEditQuotation(q)}
                            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm"
                            data-testid={`edit-comercial-quote-${q.id}`}
                            title="Editar cotización"
                          >
                            <Edit3 className="w-4 h-4" />
                            Editar
                          </button>
                          <button
                            onClick={() => handleDownloadPDF(q.id)}
                            className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-sm"
                            data-testid={`download-comercial-pdf-${q.id}`}
                          >
                            <Download className="w-4 h-4" />
                            PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No hay cotizaciones todavía</p>
              <p className="text-sm mt-1">Sube una cotización de proveedor para comenzar</p>
            </div>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}

export default ComercializadoraTab;
